import React, { useRef, useState } from 'react'
import { Modal } from '~/components/Modal/Modal'
import { CustomButton } from '~/components/CustomButton/CustomButton'
import { FormattedMessage, useIntl } from 'react-intl'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import CloseIcon from '@mui/icons-material/Close'
import axios from 'axios'
import styles from './RequestOfferModal.module.css'

const MAX_PHOTOS = 3
const MAX_FILE_SIZE = 25 * 1024 * 1024
const TARGET_SIZE = 1280
const JPEG_QUALITY = 0.82

const compressImage = (file: File): Promise<File> =>
  new Promise((resolve) => {
    const img = new window.Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      const scale = Math.min(1, TARGET_SIZE / Math.max(img.width, img.height))
      const w = Math.round(img.width * scale)
      const h = Math.round(img.height * scale)
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      canvas.getContext('2d')!.drawImage(img, 0, 0, w, h)
      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(file); return }
          resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }))
        },
        'image/jpeg',
        JPEG_QUALITY
      )
    }
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file) }
    img.src = url
  })

interface RequestOfferModalProps {
  isOpen: boolean
  onClose: () => void
  configUrl: string
  productSummary: string
}

export const RequestOfferModal: React.FC<RequestOfferModalProps> = ({
  isOpen,
  onClose,
  configUrl,
  productSummary,
}) => {
  const intl = useIntl()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [comment, setComment] = useState('')
  const [photos, setPhotos] = useState<File[]>([])

  const [nameError, setNameError] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [photoError, setPhotoError] = useState('')

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const validatePhone = (value: string): boolean => {
    const digits = value.replace(/\D/g, '')
    return digits.length === 8 && /^[067]/.test(digits)
  }

  const validateEmail = (value: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

  const validate = (): boolean => {
    let valid = true

    if (!name.trim()) {
      setNameError(intl.formatMessage({ id: 'checkout.error.required' }))
      valid = false
    } else {
      setNameError('')
    }

    if (!phone.trim()) {
      setPhoneError(intl.formatMessage({ id: 'checkout.error.required' }))
      valid = false
    } else if (!validatePhone(phone)) {
      setPhoneError(intl.formatMessage({ id: 'homepage.form.phoneInvalid' }))
      valid = false
    } else {
      setPhoneError('')
    }

    if (email.trim() && !validateEmail(email.trim())) {
      setEmailError(intl.formatMessage({ id: 'homepage.form.emailInvalid' }))
      valid = false
    } else {
      setEmailError('')
    }

    return valid
  }

  const handlePhotos = (files: FileList | null) => {
    if (!files) return
    setPhotoError('')

    const incoming = Array.from(files)
    const available = MAX_PHOTOS - photos.length

    if (incoming.length > available) {
      setPhotoError(intl.formatMessage({ id: 'homepage.form.photoLimit' }))
      return
    }

    const oversized = incoming.find((f) => f.size > MAX_FILE_SIZE)
    if (oversized) {
      setPhotoError(intl.formatMessage({ id: 'homepage.form.photoTooLarge' }))
      return
    }

    setPhotos((prev) => [...prev, ...incoming])
  }

  const removePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index))
    setPhotoError('')
  }

  const buildMessageText = (): string => {
    const fullPhone = `+373${phone.replace(/\D/g, '')}`
    let text = `<b>📞 Solicitare consultare gratuită</b>
<b>Nume:</b> ${name.trim()}
<b>Telefon:</b> ${fullPhone}`
    if (email.trim()) text += `\n<b>Email:</b> ${email.trim()}`
    text += `\n<b>Produs:</b> ${productSummary}`
    text += `\n<b>Link configurație:</b> ${configUrl}`
    if (comment.trim()) text += `\n<b>Comentariu:</b> ${comment.trim()}`
    return text
  }

  const handleSubmit = async () => {
    if (!validate()) return
    setIsSubmitting(true)

    try {
      const text = buildMessageText()

      if (photos.length > 0) {
        const compressed = await Promise.all(photos.map(compressImage))
        const formData = new FormData()
        formData.append('text', text)
        compressed.forEach((file) => formData.append('photos', file))
        await axios.post('/api/contact-form-photos', formData)
      } else {
        await axios.post('/api/contact-form', { text })
      }

      setIsSuccess(true)
    } catch {
      setPhoneError(
        intl.formatMessage({
          id: 'homepage.modal.requestOffer.error',
          defaultMessage: 'A apărut o eroare. Încercați din nou.',
        })
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    onClose()
    setTimeout(() => {
      setName('')
      setPhone('')
      setEmail('')
      setComment('')
      setPhotos([])
      setNameError('')
      setPhoneError('')
      setEmailError('')
      setPhotoError('')
      setIsSuccess(false)
    }, 300)
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      {isSuccess ? (
        <div className={styles.successContainer}>
          <CheckCircleIcon className={styles.successIcon} />
          <h3 className={styles.successTitle}>
            <FormattedMessage id="homepage.modal.requestOffer.success.title" />
          </h3>
          <p className={styles.successText}>
            <FormattedMessage id="homepage.modal.requestOffer.success" />
          </p>
          <CustomButton variant="primary" size="medium" onClick={handleClose}>
            <FormattedMessage id="homepage.button.backToPurchase" />
          </CustomButton>
        </div>
      ) : (
        <div className={styles.container}>
          <h3 className={styles.title}>
            <FormattedMessage id="homepage.modal.requestOffer.title" />
          </h3>
          <p className={styles.subtitle}>
            <FormattedMessage id="homepage.modal.requestOffer.subtitle" />
          </p>

          <div className={styles.form}>
            <input
              className={`${styles.input} ${nameError ? styles.inputError : ''}`}
              type="text"
              placeholder={intl.formatMessage({ id: 'homepage.form.name' })}
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setNameError('')
              }}
            />
            {nameError && <p className={styles.errorText}>{nameError}</p>}

            <div className={`${styles.phoneRow} ${phoneError ? styles.inputError : ''}`}>
              <span className={styles.phonePrefix}>+373</span>
              <input
                className={styles.phoneInput}
                type="tel"
                inputMode="numeric"
                maxLength={8}
                placeholder="6X XXX XXX"
                value={phone}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 8)
                  setPhone(val)
                  setPhoneError('')
                }}
              />
            </div>
            {phoneError && <p className={styles.errorText}>{phoneError}</p>}

            <input
              className={`${styles.input} ${emailError ? styles.inputError : ''}`}
              type="email"
              placeholder={intl.formatMessage({ id: 'homepage.form.email' })}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setEmailError('')
              }}
            />
            {emailError && <p className={styles.errorText}>{emailError}</p>}

            <textarea
              className={styles.textarea}
              placeholder={intl.formatMessage({ id: 'homepage.form.comment' })}
              value={comment}
              rows={3}
              onChange={(e) => setComment(e.target.value)}
            />

            <div className={styles.attachSection}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                  handlePhotos(e.target.files)
                  e.target.value = ''
                }}
              />
              {photos.length < MAX_PHOTOS && (
                <button
                  type="button"
                  className={styles.attachButton}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <AttachFileIcon className={styles.attachIcon} />
                  <FormattedMessage id="homepage.form.attachPhotos" />
                </button>
              )}
              {photoError && <p className={styles.errorText}>{photoError}</p>}
              {photos.length > 0 && (
                <div className={styles.thumbnails}>
                  {photos.map((file, i) => (
                    <div key={`${file.name}-${i}`} className={styles.thumbnail}>
                      <img src={URL.createObjectURL(file)} alt="" />
                      <button
                        type="button"
                        className={styles.thumbnailRemove}
                        onClick={() => removePhoto(i)}
                      >
                        <CloseIcon style={{ fontSize: 14 }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <CustomButton
            variant="primary"
            size="medium"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            <FormattedMessage
              id={
                isSubmitting
                  ? 'homepage.button.sending'
                  : 'homepage.button.requestOffer'
              }
            />
          </CustomButton>

          {configUrl && (
            <p className={styles.configLink}>
              <FormattedMessage id="homepage.modal.requestOffer.configAttached" />
            </p>
          )}
        </div>
      )}
    </Modal>
  )
}
